"use client"

import Image from "next/image"
import Link from "next/link"
import { useMemo, useState } from "react"
import {
  Copy,
  Download,
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Share2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  buildFounderVCard,
  companyProfile,
  founderProfiles,
  type FounderProfile,
  socialLinks,
} from "@/lib/founders"

type FounderProfileCardProps = {
  founder: FounderProfile
}

const socialIcons = {
  Facebook,
  Instagram,
  LinkedIn: Linkedin,
} as const

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#07112f]"

export default function FounderProfileCard({ founder }: FounderProfileCardProps) {
  const [shareLabel, setShareLabel] = useState("Share Card")

  const vCardHref = useMemo(() => {
    const vCard = buildFounderVCard(founder)
    return `data:text/vcard;charset=utf-8,${encodeURIComponent(vCard)}`
  }, [founder])

  const handleShare = async () => {
    const shareData = {
      title: `${founder.fullName} | ${companyProfile.name}`,
      text: `${founder.fullName} · ${founder.title} at ${companyProfile.name}`,
      url: founder.profileUrl,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
        setShareLabel("Shared")
        return
      } catch {
      }
    }

    await navigator.clipboard.writeText(founder.profileUrl)
    setShareLabel("Link Copied")
    window.setTimeout(() => setShareLabel("Share Card"), 1800)
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(0,191,166,0.18),_transparent_30%),linear-gradient(135deg,#07112f_0%,#0d1b4d_45%,#07112f_100%)] text-slate-50">
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.04)_0%,transparent_35%,rgba(0,191,166,0.08)_100%)]" />
      <div className="absolute left-1/2 top-16 h-56 w-56 -translate-x-1/2 rounded-full bg-electric-teal/20 blur-3xl sm:top-24 sm:h-72 sm:w-72" />

      <div className="relative mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6 sm:py-10 lg:gap-10 lg:px-8 lg:py-14">
        <div>
          <h1 className="font-poppins text-2xl font-semibold text-white sm:text-4xl lg:text-5xl">SKILLFITS TEAM</h1>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[1.35fr_0.85fr] lg:gap-8">
          <Card className="overflow-hidden rounded-[28px] border border-white/12 bg-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl sm:rounded-[32px]">
            <CardContent className="p-0">
              <div className="grid gap-0 lg:grid-cols-[0.78fr_1.22fr]">
                <div className="relative flex min-h-[260px] items-center justify-center overflow-hidden bg-[linear-gradient(180deg,rgba(0,191,166,0.18),rgba(7,17,47,0.2))] p-6 sm:min-h-[360px] sm:p-8 lg:min-h-0">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(0,255,255,0.22),transparent_35%)]" />
                  <div className="absolute inset-6 rounded-[28px] border border-electric-teal/35 shadow-[0_0_40px_rgba(0,191,166,0.35)] sm:inset-8 sm:rounded-[32px]" />
                  <div className="relative h-48 w-48 overflow-hidden rounded-[24px] border border-white/20 bg-white/10 shadow-[0_0_60px_rgba(0,191,166,0.45)] sm:h-64 sm:w-64 sm:rounded-[28px] lg:h-56 lg:w-56 xl:h-72 xl:w-72">
                    <Image
                      src={founder.photo}
                      alt={founder.fullName}
                      fill
                      className="object-cover object-center"
                      sizes="(max-width: 640px) 192px, (max-width: 1024px) 256px, 320px"
                      priority
                    />
                  </div>
                </div>

                <div className="flex flex-col justify-between gap-6 p-6 sm:gap-8 sm:p-8 lg:p-8 xl:p-12">
                  <div>
                    <h2 className="mt-2 font-poppins text-3xl font-semibold leading-tight text-white sm:mt-3 sm:text-4xl lg:text-5xl">
                      {founder.fullName}
                    </h2>
                    <p className="mt-3 text-base font-medium text-[#7ff7ea] sm:text-lg">{founder.title}</p>
                    <p className="mt-4 text-xs uppercase tracking-[0.3em] text-slate-200/90 sm:text-sm">
                      {companyProfile.name}
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Button
                      asChild
                      className="h-auto min-h-12 rounded-2xl bg-electric-teal px-4 py-3 text-midnight-blue hover:bg-[#3ff5e0]"
                    >
                      <a
                        href={vCardHref}
                        download={`${founder.id}.vcf`}
                        className="flex w-full items-center justify-center gap-2 text-center font-semibold leading-tight"
                      >
                        <Download className="h-4 w-4 shrink-0" />
                        Save Contact
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="h-auto min-h-12 rounded-2xl border-white/20 bg-white/10 px-4 py-3 text-slate-50 hover:bg-white/15 hover:text-white"
                    >
                      <a
                        href={`tel:${founder.phone.replace(/\s+/g, "")}`}
                        className="flex w-full items-center justify-center gap-2 text-center font-semibold leading-tight"
                      >
                        <Phone className="h-4 w-4 shrink-0" />
                        Call Now
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="h-auto min-h-12 rounded-2xl border-white/20 bg-white/10 px-4 py-3 text-slate-50 hover:bg-white/15 hover:text-white"
                    >
                      <a
                        href={`mailto:${founder.email}`}
                        className="flex w-full items-center justify-center gap-2 text-center font-semibold leading-tight"
                      >
                        <Mail className="h-4 w-4 shrink-0" />
                        Send Email
                      </a>
                    </Button>
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="h-auto min-h-12 rounded-2xl border-white/20 bg-white/10 px-4 py-3 text-slate-50 hover:bg-white/15 hover:text-white"
                    >
                      <Share2 className="h-4 w-4 shrink-0" />
                      {shareLabel}
                    </Button>
                    <Button
                      asChild
                      className="h-auto min-h-12 rounded-2xl bg-white px-4 py-3 text-midnight-blue hover:bg-white/90 sm:col-span-2"
                    >
                      <Link
                        href="/"
                        className="flex w-full items-center justify-center gap-2 text-center font-semibold leading-tight"
                      >
                        <Copy className="h-4 w-4 shrink-0" />
                        Start Your Journey
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-[24px] border border-white/12 bg-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.28)] backdrop-blur-2xl sm:rounded-[28px]">
              <CardContent className="p-5 sm:p-6 lg:p-7">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-200/85 sm:text-sm">Founders</p>
                <div className="mt-4 grid gap-3 sm:mt-5">
                  {founderProfiles.map((profile) => {
                    const isActive = profile.id === founder.id

                    return (
                      <Link
                        key={profile.id}
                        href={`/contact/${profile.id}`}
                        className={`group flex items-center gap-4 rounded-2xl border px-4 py-3 transition sm:py-4 ${FOCUS_RING} ${
                          isActive
                            ? "border-electric-teal/60 bg-electric-teal/15 shadow-[0_0_30px_rgba(0,191,166,0.18)]"
                            : "border-white/15 bg-white/10 hover:border-white/25 hover:bg-white/15"
                        }`}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-2xl border border-white/15 bg-white/10 sm:h-14 sm:w-14">
                          <Image src={profile.photo} alt={profile.fullName} fill className="object-cover" sizes="56px" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-poppins text-sm font-medium text-white sm:text-base">{profile.fullName}</p>
                          <p className="truncate text-xs text-slate-200/85 sm:text-sm">{profile.title}</p>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[24px] border border-white/12 bg-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.28)] backdrop-blur-2xl sm:rounded-[28px]">
              <CardContent className="p-5 sm:p-6 lg:p-7">
                <div className="grid gap-4">
                  <InfoPill icon={MapPin} label="Location" value={companyProfile.location} />
                  <InfoPill icon={Globe} label="Website" value="skillfits.com" href={companyProfile.website} />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[24px] border border-white/12 bg-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.28)] backdrop-blur-2xl sm:rounded-[28px]">
              <CardContent className="p-5 sm:p-6 lg:p-7">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-200/85 sm:text-sm">Connect with SkillFits</p>
                <div className="mt-4 flex flex-wrap gap-3 sm:mt-5">
                  {socialLinks.map((social) => {
                    const Icon = socialIcons[social.platform as keyof typeof socialIcons]

                    return (
                      <a
                        key={social.platform}
                        href={social.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-medium text-slate-100 transition hover:border-electric-teal/50 hover:bg-electric-teal/10 hover:text-white ${FOCUS_RING}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {social.platform}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="px-1 text-sm text-slate-200/80">
              <p>© 2026 {companyProfile.name}.</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

function InfoPill({
  icon: Icon,
  label,
  value,
  href,
  className,
}: {
  icon: typeof MapPin
  label: string
  value: string
  href?: string
  className?: string
}) {
  const content = (
    <div
      className={`rounded-2xl border border-white/15 bg-white/10 p-4 transition hover:border-electric-teal/35 hover:bg-white/15 ${className ?? ""}`}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-electric-teal/12 p-2 text-electric-teal">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-[0.24em] text-slate-200/80">{label}</p>
          <p className="mt-1 text-sm font-medium leading-6 text-white break-words">{value}</p>
        </div>
      </div>
    </div>
  )

  if (!href) {
    return content
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" className={`block rounded-2xl ${FOCUS_RING}`}>
      {content}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  )
}